const rawBase = process.env.REACT_APP_API_BASE || '';
const base = rawBase.replace(/\/$/, '');
const apiBase = base.endsWith('/api') ? base : `${base}/api`;
const join = (p) => (p && p.startsWith('/') ? p : `/${p || ''}`);
export const getApiBase = () => apiBase;
export const getAuthHeaders = () => authHeaders();

const SIGNUP_REF_STORAGE_KEY = 'mp:signupRef';

let authToken = '';
let refreshAuthToken = null;
let unauthorizedHandler = null;
let unauthorizedHandled = false;
export const setAuthToken = (token) => {
  authToken = token || '';
};
export const setAuthTokenRefresher = (fn) => {
  refreshAuthToken = typeof fn === 'function' ? fn : null;
};
export const setUnauthorizedHandler = (fn) => {
  unauthorizedHandler = typeof fn === 'function' ? fn : null;
  unauthorizedHandled = false;
};

function toDetailMessage(detail) {
  if (!detail) return '';
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'object') {
    const path = typeof detail.path === 'string' && detail.path ? `${detail.path}: ` : '';
    const message = typeof detail.message === 'string' ? detail.message : JSON.stringify(detail);
    return `${path}${message}`.trim();
  }
  return String(detail);
}

function buildApiErrorMessage(payload, fallback) {
  if (!payload || typeof payload !== 'object') return fallback;
  const base = payload.error || payload.message || fallback;
  const details = Array.isArray(payload.details)
    ? payload.details.map(toDetailMessage).filter(Boolean)
    : [];

  if (!details.length) return base;
  const detailSummary = details.slice(0, 3).join(' | ');
  return detailSummary ? `${base} (${detailSummary})` : base;
}

async function parseApiErrorResponse(res, fallbackMessage) {
  let message = fallbackMessage;
  let payload = null;
  let details = [];

  try {
    const text = await res.text();
    if (text) {
      try {
        payload = JSON.parse(text);
        message = buildApiErrorMessage(payload, fallbackMessage);
        if (Array.isArray(payload.details)) {
          details = payload.details;
        }
      } catch (_) {
        message = text;
      }
    }
  } catch (_) {
    /* ignore body parse issues */
  }

  return { message, payload, details };
}

const authHeaders = () => (authToken ? { Authorization: `Bearer ${authToken}` } : {});

function getPendingSignupRef() {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return '';
  }
  try {
    return (sessionStorage.getItem(SIGNUP_REF_STORAGE_KEY) || '').trim().toLowerCase();
  } catch (_) {
    return '';
  }
}

export function setPendingSignupRef(signupRef) {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    const normalized = typeof signupRef === 'string' ? signupRef.trim().toLowerCase() : '';
    if (normalized) {
      sessionStorage.setItem(SIGNUP_REF_STORAGE_KEY, normalized);
    } else {
      sessionStorage.removeItem(SIGNUP_REF_STORAGE_KEY);
    }
  } catch (_) {
    // Ignore storage failures.
  }
}

export function clearPendingSignupRef() {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    sessionStorage.removeItem(SIGNUP_REF_STORAGE_KEY);
  } catch (_) {
    // Ignore storage failures.
  }
}

async function requestWithAuthRetry(url, options = {}) {
  const send = async () => {
    const signupRef = getPendingSignupRef();
    const res = await fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        ...authHeaders(),
        ...(signupRef ? { 'X-MP-Signup-Ref': signupRef } : {}),
      },
    });

    if (signupRef && res.ok) {
      clearPendingSignupRef();
    }

    return res;
  };

  let res = await send();

  // If token expired/stale, try a single forced refresh then retry once.
  if (res.status === 401 && refreshAuthToken) {
    try {
      await refreshAuthToken();
      res = await send();
    } catch (_) {
      // Keep original 401 handling below.
    }
  }

  if (res.status === 401 && unauthorizedHandler && !unauthorizedHandled) {
    unauthorizedHandled = true;
    try {
      await unauthorizedHandler();
    } catch (_) {
      // Ignore sign-out failures and preserve the 401 for the caller.
    }
  }

  return res;
}

export async function apiGet(path, options = {}) {
  const url = `${apiBase}${join(path)}`;
  const res = await requestWithAuthRetry(url, options);
  if (!res.ok) {
    const fallbackMessage = `${res.status} ${res.statusText || 'Request failed'}`.trim();
    const { message, payload, details } = await parseApiErrorResponse(res, fallbackMessage);
    console.warn('[apiGet] Failed', { url, status: res.status, statusText: res.statusText, message });
    const err = new Error(message);
    err.status = res.status;
    err.payload = payload;
    err.details = details;
    throw err;
  }
  return res.json();
}

export async function apiPost(path, body) {
  const url = `${apiBase}${join(path)}`;
  const res = await requestWithAuthRetry(url, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const fallbackMessage = `${res.status} ${res.statusText || 'Request failed'}`.trim();
    const { message, payload, details } = await parseApiErrorResponse(res, fallbackMessage);
    console.warn('[apiPost] Failed', { url, status: res.status, statusText: res.statusText, message });
    const err = new Error(message);
    err.status = res.status;
    err.payload = payload;
    err.details = details;
    throw err;
  }
  return res.json();
}

export async function apiPatch(path, body) {
  const url = `${apiBase}${join(path)}`;
  const res = await requestWithAuthRetry(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const fallbackMessage = `${res.status} ${res.statusText || 'Request failed'}`.trim();
    const { message, payload, details } = await parseApiErrorResponse(res, fallbackMessage);
    console.warn('[apiPatch] Failed', { url, status: res.status, statusText: res.statusText, message });
    const err = new Error(message);
    err.status = res.status;
    err.payload = payload;
    err.details = details;
    throw err;
  }
  return res.json();
}

export async function apiDelete(path) {
  const url = `${apiBase}${join(path)}`;
  const res = await requestWithAuthRetry(url, { method: 'DELETE' });
  if (!res.ok) {
    const fallbackMessage = `${res.status} ${res.statusText || 'Request failed'}`.trim();
    const { message, payload, details } = await parseApiErrorResponse(res, fallbackMessage);
    console.warn('[apiDelete] Failed', { url, status: res.status, statusText: res.statusText, message });
    const err = new Error(message);
    err.status = res.status;
    err.payload = payload;
    err.details = details;
    throw err;
  }
  const text = await res.text();
  if (!text) return { ok: true };
  try { return JSON.parse(text); } catch (_) { return { ok: true, raw: text }; }
}
